import cx from 'classnames';
import { t } from 'i18next';
import Numeral from 'numeral';
import { Component, ReactElement } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from '../../../../shared/types/common';
import { safeCharacterName, safeParseInt } from '../../../../../../shared/common/utils';
import { withSiteMeta } from '../../../../../../shared/frontend/HOCS/withSiteMeta';
import { createActionSetError, TError } from '../../../../../../shared/frontend/scripts/actions/errorActions';
import KeystoneActionCreator from '../../actions/keystoneActionCreator';
import { namedRoutes, resolveRoutePath } from '../../bundles/routes';
import frontendLog from '../../libs/frontendLog';
import {
  TApplicationState,
  TKeystoneCharacterRankingsClientState,
  TKeystoneCharacterRankingsServerState
} from '../../libs/store';
import { areFieldsDifferent } from '../../../../../../shared/frontend/libs/utils';
import apiService, { TGetKeystoneCharacterRankingsParams } from '../../services/apiService';
import { ClassIcon } from '../../../../../../shared/frontend/scripts/wow/components/ClassIcon';
import { GuildRank } from '../presentational/guildRank';
import { Link, PossibleLink } from '../../../../../../shared/frontend/components/link';
import { Spinner } from '../../../../../../shared/frontend/components/presentational/spinner';
import { PossibleStreamTooltip } from '../../../../../../shared/frontend/components/presentational/streamPreview';
import { getSeasonAnchor } from '../../keystoneSpecRankings/utils';

type TPropsFromState = any; // FIXME: type this!

type TPropsThatDispatch = {
  setKeystoneCharacterRankings(actionData: TKeystoneCharacterRankingsClientState, instanceId: string): void;
  setKeystoneCharacterRankingsError(e: any, instanceId: string): void;
  showError(actionData: TError): void;
  showLoadingSpinner(show: boolean): void;
};

type TKeystoneCharacterRankingsProps = TPropsFromState & TPropsThatDispatch & RouteComponentProps<any, any>;

const KeystoneCharacterRankingsTableSmall = withSiteMeta((props: any) => {
  const seasonAnchor = props.season
    ? getSeasonAnchor(props.season)
    : {
        hash: '',
        query: ''
      };

  return (
    <table
      className={cx(
        'slds-table',
        'slds-table--bordered',
        'slds-table--striped',
        props.compact ? 'slds-table--no-cell-buffer' : 'slds-table--cell-buffer',
        'rio-rankings-table'
      )}
    >
      <thead>
        <tr>
          <th colSpan={3} className="slds-text-align--center text-white slds-text-heading--small">
            {props.title}
          </th>
        </tr>
      </thead>
      <tbody>
        {props.characters.length === 0 && (
          <tr>
            <td colSpan={3}>
              <div
                className="slds-p-around--large slds-text-align--center slds-align--absolute-center slds-text-heading--medium"
                style={{ minHeight: '250px', whiteSpace: 'normal' }}
              >
                {t('common:errors.empty-character-leaderboard')}
              </div>
            </td>
          </tr>
        )}

        {props.characters.map((entry: any) => (
          <tr
            key={entry.rank}
            className={cx(
              'mythic-plus-rankings--row',
              `faction--${entry.character.faction}`,
              'patron-level',
              'patron-level-bg-gradient',
              `patron-level-bg-gradient--${entry.patronLevel?.level || 0}`
            )}
          >
            <td data-label="Rank" className="slds-text-align--center rio-takeover-hidden">
              <GuildRank rank={entry.rank} small={true} />
            </td>
            <td className="slds-text-align--left">
              <div className="slds-tile slds-media">
                {/* <span
                  className={
                    `slds-align--absolute-center slds-m-right--x-small slds-avatar ` +
                    `rio-shadow--small slds-avatar--small icon-faction-${entry.character.faction}`
                  }
                /> */}
                <span className="slds-avatar slds-avatar--small rio-border--light rio-shadow--small slds-align--absolute-center slds-m-right--x-small">
                  <ClassIcon class={entry.character.class.slug} title={entry.character.class.name} />
                </span>
                <div className="slds-media__body">
                  <h3
                    className={`slds-truncate slds-text-heading--small ${
                      entry.character.stream ? 'streaming-now' : ''
                    }`}
                  >
                    <PossibleStreamTooltip stream={entry.character.stream} hide={entry.character.anonymized}>
                      <PossibleLink
                        allowLink={!entry.character.anonymized}
                        to={
                          resolveRoutePath(namedRoutes.CharacterDetails, {
                            region: entry.character.region.slug,
                            realm: entry.character.realm.slug,
                            character: entry.character.name
                          }) + seasonAnchor.query
                        }
                        className={`class-color--${entry.character.class.id}`}
                      >
                        {safeCharacterName(entry.character.name)}
                      </PossibleLink>
                    </PossibleStreamTooltip>
                  </h3>
                  <div className="slds-tile__detail slds-text-body--small">
                    <PossibleLink
                      allowLink={!entry.character.anonymized}
                      to={resolveRoutePath(namedRoutes.RegionHome, {
                        region: entry.character.region.slug
                      })}
                      className="rio-realm-link"
                    >
                      ({t(`regions:${entry.character.region.slug}.short-name`)})
                    </PossibleLink>{' '}
                    <PossibleLink
                      allowLink={!entry.character.anonymized}
                      to={resolveRoutePath(namedRoutes.RealmHome, {
                        region: entry.character.region.slug,
                        realm: entry.character.realm.slug
                      })}
                      className="rio-realm-link"
                    >
                      {entry.character.realm.name}
                    </PossibleLink>
                  </div>
                </div>
              </div>
            </td>
            <td className="slds-text-align--right">
              <PossibleLink
                allowLink={!entry.character.anonymized}
                to={
                  resolveRoutePath(namedRoutes.CharacterDetails, {
                    region: entry.character.region.slug,
                    realm: entry.character.realm.slug,
                    character: entry.character.name
                  }) + seasonAnchor.query
                }
                style={entry.scoreColor ? { color: entry.scoreColor } : {}}
                className="rio-realm-link slds-text-body--large"
                title="Mythic+ Score"
              >
                {Numeral(Math.round(entry.score)).format('0')}
              </PossibleLink>
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={3} className="slds-text-align--center slds-text-body--small">
            <Link
              className="rio-realm-link--muted"
              to={resolveRoutePath(props.realm?.slug ? namedRoutes.KeystoneCharacterRealmRankings : namedRoutes.KeystoneCharacterRankings, {
                season: props.season,
                region: props.region?.slug || 'world',
                class: 'all',
                role: props.role || 'all',
                realm: props.realm?.slug || undefined
              })}
            >
              {t('common:leaderboard.view-leaderboard-prompt')}
            </Link>
          </td>
        </tr>
      </tfoot>
    </table>
  );
});

function KeystoneRunRankingsContent(props: any) {
  if (!props.rankedCharacters) {
    return <Spinner />;
  }

  const loadingSpinner = props.isLoading ? <Spinner /> : null;

  return (
    <>
      {loadingSpinner}

      <KeystoneCharacterRankingsTableSmall
        season={props.season}
        region={props.region}
        realm={props.realm}
        characters={props.rankedCharacters}
        title={props.title}
        dungeons={props.dungeons}
        match={props.match}
        role={props.role}
        compact={props.compact}
      />
    </>
  );
}

/**
 * KeystoneCharacterRankings
 */
class KeystoneCharacterRankings extends Component<TKeystoneCharacterRankingsProps, {}> {
  public render(): ReactElement<TKeystoneCharacterRankingsProps> {
    return <KeystoneRunRankingsContent {...this.props} />;
  }
}

// take state from store and convert into props
function mapStateToProps(state: TApplicationState, ownProps: any): TPropsFromState {
  return {
    ...state.keystoneCharacterRankings.instances[ownProps.instanceId],
    dungeons: state.siteMeta.dungeons,
    currentSeasonSlug: state.siteConfig.currentSeasonSlug
  };
}

// return props that need access to `dispatch` function (props that can modify the store?)
function mapDispatchToProps(dispatch: Function, ownProps: TKeystoneCharacterRankingsProps): TPropsThatDispatch {
  return {
    setKeystoneCharacterRankings(rankings: TKeystoneCharacterRankingsClientState, instanceId: string) {
      dispatch(KeystoneActionCreator.setKeystoneCharacterRankings(rankings, instanceId));
    },
    setKeystoneCharacterRankingsError(e: any, instanceId: string) {
      dispatch(KeystoneActionCreator.setKeystoneCharacterRankingsError(instanceId, e));
    },
    showError(actionData: TError) {
      dispatch(createActionSetError(actionData));
    },
    showLoadingSpinner(show: boolean) {
      dispatch(KeystoneActionCreator.showLoadingSpinner(ownProps.instanceId, show));
    }
  };
}

// return one entire set of props
function mergeProps(stateProps: TPropsFromState, dispatchProps: TPropsThatDispatch, ownProps: any) {
  const loadParams: TGetKeystoneCharacterRankingsParams = {
    season: ownProps.season || stateProps.currentSeasonSlug,
    region: ownProps.region || 'world',
    realm: ownProps.realm || undefined,
    class: ownProps.class || 'all',
    role: ownProps.role || 'all',
    faction: ownProps.faction || undefined,
    page: safeParseInt(ownProps.page, 0),
    pageSize: ownProps.pageSize
  };

  const checkFields = ['season', 'region', 'realm', 'class', 'role', 'faction', 'page', 'pageSize'];
  const contentChanged = !stateProps.ui || areFieldsDifferent(checkFields, loadParams, stateProps.ui);
  if (!stateProps.isLoading && (!stateProps.rankedCharacters || contentChanged) && !stateProps.isError) {
    dispatchProps.showLoadingSpinner(true);

    apiService.getKeystoneCharacterRankings(loadParams, (err: any, result?: TKeystoneCharacterRankingsServerState) => {
      if (err) {
        dispatchProps.showError({
          title: t('common:errors.something-went-wrong'),
          body: err.toString()
        });
        dispatchProps.setKeystoneCharacterRankingsError(err, ownProps.instanceId);
        frontendLog.error({ err }, 'getKeystoneCharacterRankings failed');
        dispatchProps.showLoadingSpinner(false);
        return;
      }

      if (!result) {
        dispatchProps.showError({
          title: t('common:errors.something-went-wrong'),
          body: t('common:errors.unable-to-load-data-reload')
        });
        dispatchProps.setKeystoneCharacterRankingsError(
          {
            title: t('common:errors.something-went-wrong'),
            body: t('common:errors.unable-to-load-data-reload')
          },
          ownProps.instanceId
        );
        frontendLog.error('getKeystoneCharacterRankings got empty result');
        dispatchProps.showLoadingSpinner(false);
        return;
      }

      dispatchProps.setKeystoneCharacterRankings(result.rankings, ownProps.instanceId);
      dispatchProps.showLoadingSpinner(false);
    });
  }

  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps
  };
}

export default connect<TPropsFromState, TPropsThatDispatch, TKeystoneCharacterRankingsProps>(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(KeystoneCharacterRankings);
